/**
 * PM Sliding Panels -- video de fondo opcional (media_type: video).
 *
 * NO modifica assets/sliding-panels.js (nativo Athens, protegido). Se
 * conecta exclusivamente a la API pública que ese archivo ya expone:
 * los eventos 'sliding-panel:expand' / 'sliding-panel:collapse'
 * (bubbles:true) que <sliding-panel> dispara en cada cambio de estado.
 * Este script solo escucha -- no toca expand()/collapse(), clases,
 * ARIA ni el atributo `expanded`.
 *
 * Lazy loading real: cada <source> nace con data-src (sin src real,
 * sin request) -- mismo patrón que assets/slideshow-video.js. Recien al
 * activarse un panel se elige el grupo de fuentes correcto
 * (data-pm-breakpoint="mobile"|"desktop", decidido antes del primer
 * load() para no descargar ambos) y se mueve data-src -> src.
 */
(() => {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';
  var mobileMedia = window.matchMedia(MOBILE_QUERY);
  var reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');

  function getPanelVideo(panel) {
    return panel ? panel.querySelector('.pm-sliding-panel-video') : null;
  }

  // Poster inmediato (imagen), independiente de si el video llega a
  // reproducirse -- así un panel video nunca muestra un recuadro vacío,
  // ni siquiera con prefers-reduced-motion activo.
  function ensurePoster(video) {
    if (video.getAttribute('poster')) {
      return;
    }
    var useMobile = mobileMedia.matches;
    var poster = useMobile
      ? video.dataset.pmPosterMobile || video.dataset.pmPosterDesktop
      : video.dataset.pmPosterDesktop || video.dataset.pmPosterMobile;
    if (poster) {
      video.setAttribute('poster', poster);
    }
  }

  function activateSources(video) {
    if (video.dataset.pmSourcesActivated === 'true') {
      return;
    }

    var wantsMobile =
      mobileMedia.matches &&
      video.querySelector('source[data-src][data-pm-breakpoint="mobile"]');
    var breakpoint = wantsMobile ? 'mobile' : 'desktop';
    var sources = video.querySelectorAll(
      'source[data-src][data-pm-breakpoint="' + breakpoint + '"]',
    );

    // El bloque puede tener video de escritorio sin video mobile propio
    // (fallback: reutilizar el de escritorio, ver auditoría previa).
    if (sources.length === 0) {
      breakpoint = breakpoint === 'mobile' ? 'desktop' : 'mobile';
      sources = video.querySelectorAll(
        'source[data-src][data-pm-breakpoint="' + breakpoint + '"]',
      );
    }

    sources.forEach(function (source) {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    video.dataset.pmSourcesActivated = 'true';
    video.load();
  }

  function playVideo(video) {
    if (!video) {
      return;
    }

    ensurePoster(video);

    // prefers-reduced-motion: se queda en el poster estático, sin
    // autoplay -- el contenido del panel no se bloquea ni se oculta.
    if (reducedMotionMedia.matches) {
      return;
    }

    activateSources(video);

    var attemptPlay = function () {
      video.play().catch(function () {
        // Autoplay rechazado por el navegador -- el poster permanece
        // visible, no es un error real que deba ensuciar la consola.
      });
    };

    if (video.readyState >= 3) {
      attemptPlay();
    } else {
      video.addEventListener('canplay', attemptPlay, { once: true });
    }
  }

  function pauseVideo(video) {
    if (video && !video.paused) {
      video.pause();
    }
  }

  function initInstance(container) {
    if (container.dataset.pmVideoInitialized === 'true') {
      return;
    }
    container.dataset.pmVideoInitialized = 'true';

    // Poster inicial para TODOS los videos de esta instancia, estén su
    // panel expandido o colapsado. AUDITADO: ensurePoster() solo lee
    // data-pm-poster-desktop/mobile y asigna el atributo poster (imagen
    // estática) -- nunca toca los <source data-src>, nunca llama
    // video.load() ni video.play(), así que esto no activa ni descarga
    // ningún video colapsado (lazy loading real intacto). Antes de este
    // bucle, ensurePoster() solo se ejecutaba dentro de playVideo() -- un
    // panel de video que nunca pasó por ahí (cualquiera salvo el inicial,
    // hasta su primera expansión) se quedaba sin el atributo poster en
    // absoluto, por eso se veía sin imagen hasta abrirse una vez.
    container.querySelectorAll('.pm-sliding-panel-video').forEach(function (video) {
      ensurePoster(video);
    });

    var isNearViewport = false;

    // rootMargin 200px -- mismo valor que assets/video-background.js
    // (nativo) usa para este propósito exacto: evitar reproducir/cargar
    // video cuando la sección todavía está lejos del viewport.
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          isNearViewport = entry.isIntersecting;
          var expandedPanel = container.querySelector('sliding-panel[expanded="true"]');
          var video = getPanelVideo(expandedPanel);
          if (!video) {
            return;
          }
          if (isNearViewport) {
            playVideo(video);
          } else {
            pauseVideo(video);
          }
        });
      },
      { rootMargin: '200px' },
    );
    observer.observe(container);

    // Solo un panel puede estar expandido a la vez (garantizado por
    // sliding-panels.js), pero el listener es defensivo: pausa
    // cualquier otro video de esta misma instancia antes de reproducir
    // el nuevo, y nunca toca videos de otras secciones sliding-panels.
    container.addEventListener('sliding-panel:expand', function (event) {
      var video = getPanelVideo(event.target);
      if (!video) {
        return;
      }

      container.querySelectorAll('.pm-sliding-panel-video').forEach(function (otherVideo) {
        if (otherVideo !== video) {
          pauseVideo(otherVideo);
        }
      });

      ensurePoster(video);
      // Una expansión es una interacción explícita y síncrona del usuario --
      // no debe depender de isNearViewport (variable que solo se actualiza
      // de forma asíncrona desde el callback del IntersectionObserver más
      // abajo). Gatear aquí podía dejar el video sin activar en la primera
      // expansión si el observer todavía no había disparado su primer
      // callback. isNearViewport sigue intacto y sigue siendo el único gate
      // del propio observer (pausar/reanudar según cercanía al viewport).
      playVideo(video);
    });

    container.addEventListener('sliding-panel:collapse', function (event) {
      // currentTime no se resetea: el video pausado conserva el último
      // frame como fondo visual del panel contraído.
      pauseVideo(getPanelVideo(event.target));
    });
  }

  function initAll() {
    document.querySelectorAll('sliding-panels').forEach(initInstance);
  }

  initAll();

  // Theme Editor: una sección puede recargarse (edición de bloques,
  // etc.) generando una nueva instancia de <sliding-panels> en el DOM.
  document.addEventListener('shopify:section:load', function (event) {
    var container = event.target.querySelector('sliding-panels');
    if (container) {
      initInstance(container);
    }
  });
})();
